import { useFlags } from 'launchdarkly-react-client-sdk'
import Image from 'next/image'
import { forwardRef, useCallback, useMemo, useState } from 'react'
import { AnyObject, Field, Form } from 'react-final-form'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faEye } from '@fortawesome/pro-light-svg-icons/faEye'
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash'
import { faWarning } from '@fortawesome/pro-light-svg-icons/faWarning'
import clsx from 'clsx'
import { boolean, object, string } from 'yup'

import { EventType } from '@infura/packages.api'
import { FEATURE_FLAGS } from '@infura/packages.common/constants'
import Notification from '@infura/packages.components/Notification'
import { Button } from '@infura/packages.ui/Button'
import { Checkbox } from '@infura/packages.ui/Checkbox'
import { Layout } from '@infura/packages.ui/Layout'
import { Text } from '@infura/packages.ui/Text'

import { CaptchaModal } from 'components/CaptchaModal'
import FieldInput from 'components/FieldInput'
import { Footer } from 'components/loginRegistration/footer'

import { api } from 'lib/api/api'
import {
  RegisterFormValues,
  emailValidator,
  emailValidatorNoAlias,
  getValidationErrors,
  registrationFormPasswordValidator
} from 'lib/forms/validators'

interface RegisterFormParams {
  onSubmit: ({ email, first_name, last_name, password, recaptcha }: RegisterFormValues) => Promise<AnyObject>
  errorRef: React.RefObject<HTMLElement>
}

const RegisterForm = forwardRef(({ errorRef, onSubmit }: RegisterFormParams, ref) => {
  const [termsAccepted, setTermsAccepted] = useState(false)
  const flags = useFlags()
  const noAliasEmail = useMemo(() => flags[FEATURE_FLAGS.NO_EMAIL_ALIAS], [flags])
  const termsErrorMsg = 'Please accept the terms and conditions before continuing'
  const [passwordVisible, setPasswordVisible] = useState(false)

  const validationSchema = object().shape({
    email: noAliasEmail ? emailValidatorNoAlias : emailValidator,
    first_name: string().required('Required'),
    last_name: string().required('Required'),
    password: registrationFormPasswordValidator,
    terms: boolean().isTrue(termsErrorMsg).required(termsErrorMsg)
  })

  const handleAccept = useCallback(async () => {
    setTermsAccepted(true)

    try {
      await api.createEvent({ payload: {}, type: EventType.TERMS_OF_USE_ACCEPTED })
    } catch (e: any) {}
  }, [])

  const handleDecline = useCallback(async () => {
    setTermsAccepted(false)
    try {
      await api.createEvent({ payload: {}, type: EventType.TERMS_OF_USE_DECLINED })
    } catch (e: any) {}
  }, [])

  return (
    <div className='xxl:max-w-3xl m-auto max-w-2xl p-12'>
      <Form
        onSubmit={async (values: RegisterFormValues) => {
          const isValid = await validationSchema.isValid(values)
          if (isValid) return onSubmit(values)
        }}
        ref={ref}
        render={({
          dirty,
          handleSubmit,
          hasValidationErrors,
          modifiedSinceLastSubmit,
          submitError,
          submitting: formSubmitting
        }) => {
          const submitting = formSubmitting
          const error = submitError

          return (
            <>
              <Notification
                className={clsx(
                  'mx-auto mb-8 w-full p-6',
                  error && !modifiedSinceLastSubmit ? 'visible block' : 'invisible hidden'
                )}
                color='alert'
                size='sm'
                variant='contained'
              >
                <span className='w-full leading-6' ref={errorRef}>
                  <FontAwesomeIcon className='mr-2' icon={faWarning} /> {error}
                </span>
              </Notification>
              <h2 className='text-text-default xxl:text-4xl mb-8 text-2xl font-bold'>Create an account</h2>
              <form className='xxl:text-lg text-base' onSubmit={handleSubmit}>
                <Layout className='mb-6' container='none' theme='twoColumn'>
                  <div>
                    <FieldInput
                      className='xxl:px-4 xxl:py-3 xxl:text-lg h-auto text-base leading-6'
                      id='first_name'
                      label={<span className='xxl:text-lg text-base'>First Name</span>}
                      name='first_name'
                      required
                      type='string'
                    />
                  </div>
                  <div>
                    <FieldInput
                      className='xxl:px-4 xxl:py-3 xxl:text-lg h-auto text-base'
                      id='last_name'
                      label={<span className='xxl:text-lg text-base'>Last Name</span>}
                      name='last_name'
                      required
                      type='text'
                    />
                  </div>
                </Layout>
                <div className='mb-6'>
                  <FieldInput
                    className='xxl:px-4 xxl:py-3 xxl:text-lg h-auto text-base'
                    id='email'
                    label={
                      <span className='flex'>
                        <span className='xxl:text-lg text-base'>Email</span>
                      </span>
                    }
                    name='email'
                    required
                    type='email'
                  />
                </div>
                <FieldInput
                  className='xxl:px-4 xxl:py-3 xxl:text-lg h-auto text-base'
                  icon={passwordVisible ? faEye : faEyeSlash}
                  iconPosition='right'
                  id='password'
                  label={<span className='xxl:text-lg text-base'>Password</span>}
                  name='password'
                  onIconClick={() => setPasswordVisible(!passwordVisible)}
                  pattern='.{8,}'
                  required
                  type={passwordVisible ? 'text' : 'password'}
                />
                <div className='mb-6 mt-6'>
                  <Field
                    data-testid='terms-checkbox'
                    name='terms'
                    render={({ input, meta }) => {
                      return (
                        <Checkbox
                          checked={termsAccepted}
                          className='cursor-pointer'
                          disabled={submitting}
                          error={dirty ? meta.error : null}
                          label={
                            <span
                              className='xxl:text-lg 2xxl:text-xl -mt-1 block text-base normal-case'
                              id='terms-description'
                            >
                              <span>By signing up, you confirm you have read and agree to Consensys' </span>
                              <a href='https://consensys.net/terms-of-use/' rel='noopener noreferrer' target='_blank'>
                                Terms of Use
                              </a>{' '}
                              <span>and </span>
                              <a href='https://consensys.net/privacy-policy/' rel='noopener noreferrer' target='_blank'>
                                Privacy Policy
                              </a>
                            </span>
                          }
                          // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
                          name='terms'
                          // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
                          onChange={() => {
                            if (!submitting && termsAccepted) {
                              handleDecline()
                            } else {
                              handleAccept()
                            }
                          }}
                          {...input}
                        />
                      )
                    }}
                    required
                    type='checkbox'
                  />
                </div>
                <Text as='p' className='mt-6' theme='sm'>
                  <span>
                    We collect your name and email address in order to allow you access to your account, to manage your
                    account and to contact you about changes to your account. For more information in relation to our
                    privacy practices, please see our{' '}
                  </span>
                  <a
                    className='whitespace-pre'
                    href='https://consensys.net/privacy-policy/'
                    rel='noopener noreferrer nob'
                    target='_blank'
                  >
                    Privacy Policy.
                  </a>
                </Text>
                <RegistrationSubmission submitting={submitting} valid={!hasValidationErrors} />
              </form>
              {submitting ? (
                <noscript>
                  <Image
                    alt=''
                    height='1'
                    loading='eager'
                    src='https://analytics.twitter.com/i/adsct?txn_id=o4pmz&p_id=Twitter&tw_sale_amount=0&tw_order_quantity=0'
                    style={{ display: 'none' }}
                    width='1'
                  />
                  <Image
                    alt=''
                    height='1'
                    loading='eager'
                    src='//t.co/i/adsct?txn_id=o4pmz&p_id=Twitter&tw_sale_amount=0&tw_order_quantity=0'
                    style={{ display: 'none' }}
                    width='1'
                  />
                </noscript>
              ) : null}
            </>
          )
        }}
        validate={async values => {
          return await getValidationErrors({
            validationSchema,
            values
          })
        }}
      />
      <Footer ctaLink='/login' ctaText='Login' description='Already have login and password?' />
    </div>
  )
})

RegisterForm.displayName = 'RegisterForm'

function RegistrationSubmission({ submitting, valid }: Readonly<{ valid: boolean; submitting: boolean }>) {
  const [captchaModalOpen, setCaptchaModalOpen] = useState(false)

  return (
    <>
      <Button
        className='mb-8'
        color='primary'
        data-testid='sign-up-button'
        disabled={submitting}
        loading={submitting}
        onClick={() => {
          if (valid) setCaptchaModalOpen(true)
        }}
        type='button'
      >
        Create a free account
      </Button>
      <CaptchaModal open={captchaModalOpen} setIsOpen={setCaptchaModalOpen} />
    </>
  )
}

export default RegisterForm
